<script lang="ts" setup>
import { Button } from '~ds/components/ui/button';
import { Heading, Text } from '~ds/components/ui/typography';
import { normalizePassword, PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH, passwordLength } from '~kui/constants/password';
import { useI18n } from 'vue-i18n';

interface Props {
  loading: boolean;
  /** The account the password is being created for, so password managers can file it. */
  email: string;
  /** Where the audience can add a password later, for surfaces that are not a student profile. */
  description?: string;
  /** Drops the heading and description, for a surface that says them itself (Blinky). */
  hideIntro?: boolean;
}

withDefaults(defineProps<Props>(), {
  description: undefined,
  hideIntro: false,
});

const emit = defineEmits<{
  submit: [password: string];
  skip: [];
}>();

const { t } = useI18n();

const password = ref('');
const confirmPassword = ref('');
const passwordErrors = ref<string[]>([]);
const confirmErrors = ref<string[]>([]);

const handleSubmit = () => {
  passwordErrors.value = [];
  confirmErrors.value = [];

  const newPassword = normalizePassword(password.value);

  if (passwordLength(newPassword) < PASSWORD_MIN_LENGTH) {
    passwordErrors.value = [t('kui.password.tooShort', { length: PASSWORD_MIN_LENGTH })];
    return;
  }
  if (passwordLength(newPassword) > PASSWORD_MAX_LENGTH) {
    passwordErrors.value = [t('kui.password.tooLong')];
    return;
  }
  if (newPassword !== normalizePassword(confirmPassword.value)) {
    confirmErrors.value = [t('kui.password.mismatch')];
    return;
  }

  emit('submit', newPassword);
};

const setError = (message: string) => {
  passwordErrors.value = [message];
};

defineExpose({ setError });
</script>

<template>
  <form
    class="flex flex-col items-center"
    data-testid="auth-password-setup-step"
    @submit.prevent="handleSubmit"
  >
    <template v-if="!hideIntro">
      <Heading
        size="xl"
        as="h1"
        class="text-center mb-3"
      >
        {{ t('kui.auth.createPasswordHeading') }}
      </Heading>
      <Text size="sm" class="text-center mb-6">
        {{ description ?? t('kui.auth.createPasswordDescription') }}
      </Text>
    </template>
    <KUICoreHiddenUsernameField :email="email" />
    <div class="w-full">
      <KUICoreInputText
        v-model="password"
        :label="t('kui.password.newPassword')"
        hide-label
        type="password"
        autocomplete="new-password"
        :placeholder="t('kui.password.newPassword')"
        :error-messages="passwordErrors"
        required
        data-testid="auth-password-setup-field"
      />
    </div>
    <div class="mt-4 w-full">
      <KUICoreInputText
        v-model="confirmPassword"
        :label="t('kui.password.confirmPassword')"
        hide-label
        type="password"
        autocomplete="new-password"
        :placeholder="t('kui.password.confirmPassword')"
        :error-messages="confirmErrors"
        required
        data-testid="auth-password-setup-confirm"
      />
    </div>
    <Button
      class="mt-6 w-full"
      variant="secondary"
      size="lg"
      type="submit"
      :loading="loading"
      data-testid="auth-password-setup-submit"
    >
      {{ t('kui.auth.createPasswordButton') }}
    </Button>
    <Button
      class="mt-4"
      variant="link"
      size="sm"
      type="button"
      :disabled="loading"
      data-testid="auth-password-setup-skip"
      @click="emit('skip')"
    >
      {{ t('kui.auth.skipForNow') }}
    </Button>
  </form>
</template>
